import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCustomColors } from "../../hooks/useCustomColors";

export interface LiveStatRange {
  labelKey: string;
  dot: string;
  min: number;
  max: number;
  prefix?: string;
  refreshSeconds?: number;
}

interface LiveStatsProps {
  ranges: LiveStatRange[];
}

const randomValue = ({ min, max }: LiveStatRange) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const formatValue = (stat: LiveStatRange, value: number) =>
  `${stat.prefix ?? ""}${value.toLocaleString("en-MY")}`;

const LiveStats = ({ ranges }: LiveStatsProps) => {
  const colors = useCustomColors();
  const { t } = useTranslation();
  const [values, setValues] = useState(() => ranges.map(randomValue));

  useEffect(() => {
    setValues(ranges.map(randomValue));
    const intervals = ranges.map((stat, index) =>
      window.setInterval(() => {
        setValues((previous) =>
          previous.map((value, valueIndex) =>
            valueIndex === index ? randomValue(stat) : value
          )
        );
      }, (stat.refreshSeconds ?? 5) * 1000)
    );
    return () => intervals.forEach((interval) => window.clearInterval(interval));
  }, [ranges]);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, minmax(0, 1fr))",
          md: "repeat(4, minmax(0, 1fr))",
        },
        gap: "12px",
      }}
    >
      {ranges.map((stat, index) => (
        <Box
          key={stat.labelKey}
          sx={{
            minWidth: 0,
            height: "35px",
            px: { xs: "10px", md: "16px" },
            display: "flex",
            alignItems: "center",
            gap: "8px",
            borderRadius: "18px",
            background: `linear-gradient(180deg, ${colors.background.rail} 0%, ${colors.accent.primarySoft} 100%)`,
            border: `1px solid ${colors.border.primary}`,
          }}
        >
          <Box sx={{ flexShrink: 0, width: 7, height: 7, borderRadius: "50%", backgroundColor: stat.dot }} />
          <Typography noWrap sx={{ fontSize: { xs: "11.5px", md: "12.5px" }, color: colors.text.secondary, letterSpacing: { xs: 0, md: "0.5px" } }}>
            {t(stat.labelKey)}
          </Typography>
          <Typography noWrap sx={{ ml: "auto", pl: "6px", fontSize: { xs: "11.5px", md: "12.5px" }, fontWeight: 800, color: colors.accent.gold }}>
            {formatValue(stat, values[index] ?? randomValue(stat))}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default LiveStats;
